import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/lib/AuthProvider";
import { api } from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Sparkles, ChevronRight } from "lucide-react";

const FIELD_LABELS = {
  age: "Age",
  gender: "Gender",
  height_cm: "Height",
  weight_kg: "Weight",
  city: "City",
  country: "Country",
  occupation: "Occupation",
  style_preferences: "Style preferences",
  budget_preference: "Budget",
  face_photo_path: "Identity photo",
  name: "Display name",
};

const todayKey = (uid) => `stylio:profile-reminder:${uid || "anon"}:${new Date().toISOString().slice(0, 10)}`;

export default function ProfileCompletionReminder() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const [percent, setPercent] = useState(0);
  const [missing, setMissing] = useState([]);

  useEffect(() => {
    if (!user?.user_id || !user.onboarded) return;
    // Don't show on /profile (already there) or /onboarding
    if (pathname.startsWith("/profile") || pathname.startsWith("/onboarding")) return;
    // Skip if already dismissed/completed today
    if (localStorage.getItem(todayKey(user.user_id))) return;

    let cancelled = false;
    api.get("/users/me/completion").then(({ data }) => {
      if (cancelled) return;
      if ((data.percent ?? 0) >= 100) return;
      setPercent(data.percent || 0);
      setMissing(data.missing || []);
      // Delay 1.2s so it doesn't fight the page load
      const t = setTimeout(() => setOpen(true), 1200);
      return () => clearTimeout(t);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [user?.user_id, user?.onboarded, pathname]);

  const dismiss = () => {
    if (user?.user_id) localStorage.setItem(todayKey(user.user_id), "1");
    setOpen(false);
  };

  const goComplete = () => {
    if (user?.user_id) localStorage.setItem(todayKey(user.user_id), "1");
    setOpen(false);
    navigate("/profile");
  };

  const topMissing = missing.slice(0, 4).map((k) => FIELD_LABELS[k] || k);

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) dismiss(); }}>
      <DialogContent className="rounded-none max-w-md" data-testid="profile-reminder-dialog">
        <DialogHeader>
          <p className="eyebrow flex items-center gap-2"><Sparkles size={12} /> Sharpen your style</p>
          <DialogTitle className="font-serif text-3xl tracking-tight">
            Your profile is {percent}% complete.
          </DialogTitle>
        </DialogHeader>

        <p className="text-sm text-muted-foreground">
          A complete profile makes every outfit, try-on and recommendation feel uniquely yours.
        </p>

        {topMissing.length > 0 && (
          <div className="mt-1 border-l-2 border-rose-400 dark:border-rose-500 bg-rose-50/60 dark:bg-rose-950/20 pl-3 py-2">
            <p className="text-[10px] uppercase tracking-[0.18em] text-rose-600 dark:text-rose-400 font-medium">
              Still missing
            </p>
            <p className="text-sm mt-1">{topMissing.join(" · ")}{missing.length > 4 ? ` · +${missing.length - 4} more` : ""}</p>
          </div>
        )}

        {/* Progress bar */}
        <div className="mt-2">
          <div className="h-1 bg-muted overflow-hidden">
            <div
              className="h-full bg-accent transition-all duration-700"
              style={{ width: `${percent}%` }}
              data-testid="profile-reminder-progress"
            />
          </div>
        </div>

        <div className="flex gap-2 justify-end mt-5">
          <Button
            variant="outline"
            onClick={dismiss}
            data-testid="profile-reminder-skip-btn"
            className="rounded-none uppercase tracking-[0.2em] text-xs"
          >
            Skip for today
          </Button>
          <Button
            onClick={goComplete}
            data-testid="profile-reminder-complete-btn"
            className="rounded-none uppercase tracking-[0.2em] text-xs"
          >
            Complete profile <ChevronRight size={12} className="ml-1" />
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
